/**
 * JobsHome — /jobs
 *
 * Public hub: hero + search bar + browse-by-category + featured jobs.
 * Simple, keyboard-driven, no fluff.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { JOB_CATEGORIES, EMPLOYMENT_TYPES, labelFor, findGroupForSub } from "@/data/jobCategories";
import TopicNewsStrip from "@/components/TopicNewsStrip";
import { Search, MapPin, Briefcase, Plus, Loader2, Building2, User, Users, Wifi } from "lucide-react";

function JobCard({ job }) {
  const g = findGroupForSub(job.category);
  return (
    <Link
      to={`/jobs/${job.slug || job.id}`}
      className="wofy-card !p-4 flex flex-col gap-2 hover:shadow-md transition-shadow"
      data-testid={`job-card-${job.slug || job.id}`}
    >
      <div className="flex items-center gap-2">
        <span className="text-lg">{g?.emoji || "🧑‍💼"}</span>
        <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{labelFor(g?.id || "job")}</span>
        {job.remote && (
          <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                style={{ background: "var(--wofy-surface)", color: "var(--wofy-primary)" }}>
            <Wifi className="w-2.5 h-2.5" /> Remote
          </span>
        )}
      </div>
      <div className="font-display text-lg leading-tight">{job.title}</div>
      <div className="text-xs opacity-80">
        {job.business?.name ? (
          <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {job.business.name}</span>
        ) : job.poster_type === "individual" ? (
          <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {job.posted_by_name || "Individual"}</span>
        ) : null}
      </div>
      <div className="flex items-center justify-between text-[11px] opacity-70">
        <span className="inline-flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {job.remote ? "Remote" : [job.city, job.country].filter(Boolean).join(", ") || "—"}
        </span>
        <span className="font-mono uppercase text-[10px]">{labelFor(job.employment_type)}</span>
      </div>
      {job.salary && <div className="text-xs font-medium" style={{ color: "var(--wofy-primary)" }}>{job.salary}</div>}
    </Link>
  );
}

export default function JobsHome() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [empType, setEmpType] = useState(params.get("employment_type") || "");
  const [remoteOnly, setRemoteOnly] = useState(params.get("remote") === "true");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = {};
      if (q) query.q = q;
      if (category) query.category = category;
      if (empType) query.employment_type = empType;
      if (remoteOnly) query.remote = true;
      const { data } = await http.get("/jobs", { params: query });
      setItems(data.items || []); setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [q, category, empType, remoteOnly]);

  useEffect(() => { load(); }, [load]);

  const runSearch = () => {
    const p = {};
    if (q) p.q = q;
    if (category) p.category = category;
    if (empType) p.employment_type = empType;
    if (remoteOnly) p.remote = "true";
    setParams(p);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="jobs-home">
      {/* Latest Jobs & Business news */}
      <TopicNewsStrip topic="Jobs" q="jobs OR hiring OR employment" testId="jobs-news-strip" />
      {/* Hero */}
      <section className="rounded-xl p-6 md:p-10 mb-8" style={{ background: "var(--wofy-surface)" }}>
        <div className="overline mb-2">SOCIAL PULSE JOBS & GIGS</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-3">Find work. Hire talent. Help your community.</h1>
        <p className="max-w-2xl opacity-80">
          From a full-time accountant to a weekend babysitter, from wedding photographers to disaster-relief volunteers —
          Social Pulse Jobs connects local businesses, communities and neighbours.
        </p>

        {/* Search bar */}
        <div className="mt-5 flex flex-wrap items-center gap-2 wofy-card !p-3">
          <div className="flex items-center gap-1.5 pl-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 opacity-60" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Search 'React developer' or 'babysitter in Kochi'"
              className="text-sm bg-transparent outline-none flex-1"
              data-testid="jobs-home-search-input"
            />
          </div>
          <div className="flex items-center gap-1 border-l pl-2" style={{ borderColor: "var(--wofy-border)" }}>
            <Briefcase className="w-3.5 h-3.5 opacity-60" />
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="text-sm bg-transparent outline-none" data-testid="jobs-home-category">
              <option value="">All categories</option>
              {Object.entries(JOB_CATEGORIES).flatMap(([gid, g]) =>
                g.subs.map((s) => <option key={`${gid}-${s}`} value={s}>{labelFor(s)} ({g.label})</option>))}
            </select>
          </div>
          <div className="flex items-center gap-1 border-l pl-2" style={{ borderColor: "var(--wofy-border)" }}>
            <select value={empType} onChange={(e) => setEmpType(e.target.value)} className="text-sm bg-transparent outline-none" data-testid="jobs-home-emp-type">
              <option value="">Any type</option>
              {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{labelFor(t)}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={remoteOnly} onChange={(e) => setRemoteOnly(e.target.checked)} data-testid="jobs-home-remote" />
            Remote
          </label>
          <button onClick={runSearch} className="wofy-btn-primary" data-testid="jobs-home-search-btn">Search</button>
          {user && (
            <Link to="/jobs/post" className="wofy-btn-ghost inline-flex items-center gap-1.5" data-testid="jobs-home-post-cta">
              <Plus className="w-4 h-4" /> Post a job
            </Link>
          )}
          {user && (
            <Link to="/jobs/dashboard" className="wofy-btn-ghost inline-flex items-center gap-1.5" data-testid="jobs-home-dashboard-cta">
              <Briefcase className="w-4 h-4" /> My dashboard
            </Link>
          )}
        </div>
      </section>

      {/* Browse by category */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl">Browse by category</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(JOB_CATEGORIES).map(([gid, g]) => (
            <Link
              key={gid} to={`/jobs?category=${g.subs[0]}`}
              className="wofy-card !p-4 flex items-start gap-3 hover:shadow-md transition-shadow"
              data-testid={`jobs-cat-${gid}`}
            >
              <span className="text-3xl">{g.emoji}</span>
              <div>
                <div className="font-display text-lg leading-tight">{g.label}</div>
                <div className="text-[11px] opacity-70 mt-0.5">{g.subs.length} sub-categories</div>
                <div className="text-xs opacity-80 mt-1 line-clamp-2">{g.subs.slice(0, 4).map(labelFor).join(" · ")}…</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Results */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-2xl">
            {q || category || empType || remoteOnly ? "Matching jobs" : "Latest opportunities"}
          </h2>
          <span className="text-xs opacity-70">{total} result{total === 1 ? "" : "s"}</span>
        </div>
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 mx-auto animate-spin opacity-60" /></div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center" data-testid="jobs-home-empty">
            <Users className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <p className="font-display text-xl mb-1">Be the first to post</p>
            <p className="text-sm opacity-70 mb-4">No jobs match — or none listed yet. Post one and start hiring.</p>
            {user && <Link to="/jobs/post" className="wofy-btn-primary inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Post a job</Link>}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="jobs-home-results">
            {items.map((j) => <JobCard key={j.id} job={j} />)}
          </div>
        )}
      </section>
    </div>
  );
}
